import React, {useState} from 'react';
import {useHistory, withRouter} from 'react-router-dom';
import {ButtonDropdown, DropdownHandler, FlexRow, Icon, ShortcutSignifier} from '@forecast-it/design-system';
import {trackEvent} from '../../../tracking/amplitude/TrackingV2';
import {MODAL_TYPE, showModal} from '../../shared/components/modals/generic_modal_conductor';
import {showCreateOpportunityModal} from '../../shared/components/modals/create-opportunity/CreateOpportunityModal';
import {hasAiAccess, hasPermission, hasTaskCreatePermission, isClientUser} from '../../shared/util/PermissionsUtil';
import {hasFeatureFlag} from '../../shared/util/FeatureUtil';
import {PERMISSION_TYPE} from '../../../Permissions';
import {FEATURE_FLAGS, HIDDEN_FEATURES, MODULE_TYPES} from '../../../constants';
import Util from '../../shared/util/util';
import {hasModule} from '../../shared/util/ModuleUtil';
import {isTicketingAvailable} from '../../shared/util/ticketingAvailability';
import CreateTaskMutation from '../../../mutations/create_task_mutation';
import {createFragmentContainer, graphql} from 'react-relay';
import {useIntl} from 'react-intl';
import ProgramUtil from '../../shared/util/ProgramUtil';
import CompanySetupUtil, {isFeatureHidden} from '../../shared/util/CompanySetupUtil';
import {createReport} from '../../reports-tab/reports-page/actions/create-report/CreateReport';
import ClientContactMenuItem from './ClientContactMenuItem';
import CreateClientModal from '../../clients-tab/CreateClientModal';
import CreateContactConfirmModal from '../../clients-tab/CreateContactConfirmModal';
import ClientContactModalContainer from '../../admin-tab/clients-page/ClientContactModalContainer';
import {createToast} from '../../shared/components/toasts/toast';
import {isProjectLocked} from '../../shared/util/projectStageUtil';

export const DropDownButton = formatMessage => (
	<ButtonDropdown emphasis={'high'}>{formatMessage({id: 'common.create'})}</ButtonDropdown>
);

const CreateButton = props => {
	const history = useHistory();
	const {formatMessage} = useIntl();
	const {company} = props.viewer;
	const [modalState, setModalState] = useState({type: 'none'});

	const preventCreateTimeOff =
		isClientUser() ||
		isFeatureHidden(HIDDEN_FEATURES.TIME_REGISTRATIONS) ||
		company.bambooHREnabled ||
		(hasFeatureFlag('hibob_integration') && company.hiBobEnabled && company.hiBobConnectionEnabled);

	const handleCreateProject = () => {
		trackEvent('New Button - New Project', 'Clicked');
		let rawId;
		try {
			rawId =
				props.viewer && props.viewer.actualPersonId
					? parseInt(atob(props.viewer.actualPersonId).replace('Person:', ''), 10) || undefined
					: undefined;
		} catch {
			rawId = undefined;
		}
		// AiProjectPlanningModal is the redesigned project-creation modal (TNSR-635), behind its own
		// project_create_modal_redesign flag — off by default. Once on, the forecast_agent_planning
		// flag/MANAGE_ACCOUNT_SETTINGS/AI_ACCESS gate lives inside the new modal (aiEnabled prop in
		// generic_modal_conductor.js), controlling only the SoW promo.
		//
		// Off: full parity with pre-TNSR-635 production, restoring the original routing through
		// ProjectCreationChoiceModal (Manual vs SoW) for anyone who already had forecast_agent_planning
		// — everyone else goes straight to NewProjectModalV2, same as before this PR ever shipped.
		if (hasFeatureFlag(FEATURE_FLAGS.PROJECT_CREATE_MODAL_REDESIGN)) {
			showModal({type: MODAL_TYPE.AI_PROJECT_PLANNING, personId: rawId});
		} else if (
			hasFeatureFlag('forecast_agent_planning') &&
			hasPermission(PERMISSION_TYPE.MANAGE_ACCOUNT_SETTINGS) &&
			hasAiAccess()
		) {
			showModal({type: MODAL_TYPE.PROJECT_CREATION_CHOICE, personId: rawId});
		} else {
			showModal({type: MODAL_TYPE.NEW_PROJECT_V2});
		}
	};

	const handleCreateOpportunity = () => {
		trackEvent('New Button - New Opportunity', 'Clicked');
		showCreateOpportunityModal();
	};

	const handleCreateTeamMember = () => {
		trackEvent('New Button - New Team Member', 'Clicked');
		showModal({
			type: MODAL_TYPE.ADD_TEAM_MEMBER,
			addClientUser: false,
			companyId: props.viewer.company.id,
			buttonLocation: 'Header',
		});
	};

	const handleCreateClient = () => {
		trackEvent('New Button - New Client', 'Clicked');
		if (hasFeatureFlag('new_clients_feature')) {
			setModalState({type: 'createClient'});
		} else {
			showModal({type: MODAL_TYPE.ADD_CLIENT});
		}
	};

	const handleClientCreated = (clientId, clientName) => {
		createToast({
			duration: 5000,
			message: formatMessage({id: 'clients_page.create_client.success_snackbar'}, {name: clientName}),
		});
		setModalState({type: 'createContactConfirm', clientId, clientName});
	};

	const handleCreateClientContact = () => {
		trackEvent('New Button - Create Client Contact', 'Clicked');
		showModal({type: MODAL_TYPE.CLIENT_CONTACT, entryPoint: 'header'});
	};

	const handleCreateInsight = () => {
		trackEvent('New Button - New Report', 'Clicked');
		if (!hasFeatureFlag('reports_redesign_inverted')) {
			createReport();
		} else {
			props.history.push('/reports/new/step1');
		}
	};

	const handleCreateExpenseItem = () => {
		trackEvent('New Button - New Expense Item', 'Clicked');
		showModal({
			type: MODAL_TYPE.EXPENSE_ITEM,
			projectId: props.viewer.project ? props.viewer.project.id : null,
		});
	};

	const handleCreateTask = () => {
		trackEvent('New Button - New Task', 'Clicked');

		const onSuccess = result => {
			if (result.createTask.errors && result.createTask.errors.length === 1) {
				Util.checkForSageErrorAndShowModal(result.createTask.errors, false);
			} else {
				Util.showTaskModal(result.createTask.task.node.companyTaskId, history);
			}
		};

		if (props.viewer.project && !isProjectLocked(props.viewer.project)) {
			Util.CommitSchedulingModalUpdate(
				CreateTaskMutation,
				{
					projectId: props.viewer.project.id,
					name: 'New task',
					companyId: props.viewer.company.id,
					placementOfTask: 'TOP',
				},
				onSuccess
			);
		} else {
			showModal({
				type: MODAL_TYPE.TASK_LOCATION,
				viewerId: props.viewer.id,
				currentProjectGroupId: null,
				currentProjectGroupName: null,
			});
		}
	};

	const handleCreateProjectGroup = () => {
		trackEvent('New Button - New Project Group', 'Clicked');
		showModal({
			type: MODAL_TYPE.ADD_PROJECT_GROUP,
		});
	};

	const handleCreateTimeEntry = () => {
		trackEvent('New Button - New Time Entry', 'Clicked');
		showModal({
			type: MODAL_TYPE.CREATE_TIME_REGISTRATION,
			personId: props.viewer.actualPersonId,
		});
	};

	const handleCreateTimeOff = () => {
		trackEvent('New Button - New Time Off', 'Clicked');

		showModal({
			type: MODAL_TYPE.CREATE_TIME_OFF,
			personId: props.viewer.actualPersonId,
		});
	};

	const handleCreateProgram = () => {
		trackEvent('New Button - New Program', 'Clicked');
		showModal({
			type: MODAL_TYPE.CREATE_PROGRAM,
		});
	};

	const handleCreateTicket = () => {
		trackEvent('New Button - New Ticket', 'Clicked');
		// ACC-506: reuse the ticketing create flow by navigating to the ticket
		// list with the create query param; TicketListRoute opens its existing
		// CreateTicketModal on arrival and strips the param.
		history.push('/tickets?create=1');
	};

	return (
		<>
			<DropdownHandler>
				<DropdownHandler.Trigger
					onClick={() => trackEvent('Navigation Bar Create Menu Button', 'Clicked')}
					data-cy={'create-new-button'}
				>
					{DropDownButton(formatMessage)}
				</DropdownHandler.Trigger>
				<DropdownHandler.Content offset={6}>
					{hasTaskCreatePermission() && (
						<DropdownHandler.Item onClick={handleCreateTask} data-cy={'create-new-task'}>
							<Icon icon="task" size="m" />
							{formatMessage({id: 'common.task'})}
						</DropdownHandler.Item>
					)}
					{hasPermission(PERMISSION_TYPE.PROJECTS_CREATE) && (
						<DropdownHandler.Item onClick={handleCreateProject} data-cy={'create-new-project'}>
							<Icon icon="project" size="m" />
							{formatMessage({id: 'common.project'})}
						</DropdownHandler.Item>
					)}
					{hasModule(MODULE_TYPES.PROGRAMS) && hasPermission(PERMISSION_TYPE.PROGRAMS_CREATE) && (
						<DropdownHandler.Item onClick={handleCreateProgram} data-cy={'create-new-program'}>
							<Icon icon="program" size="m" />
							{ProgramUtil.programText(formatMessage)}
						</DropdownHandler.Item>
					)}
					{hasPermission(PERMISSION_TYPE.PROJECTS_CREATE) && (
						<DropdownHandler.Item onClick={handleCreateProjectGroup} data-cy={'create-new-connected-project'}>
							<Icon icon="connectedProject" size="m" />
							{Util.titleCase(formatMessage({id: 'common.connected_project'}))}
						</DropdownHandler.Item>
					)}
					{isClientUser() || isFeatureHidden(HIDDEN_FEATURES.TIME_REGISTRATIONS) ? null : (
						<>
							<DropdownHandler.Separator />
							<DropdownHandler.Item onClick={handleCreateTimeEntry} data-cy={'create-new-time-entry'}>
								<Icon icon="timeEntry" size="m" />
								{Util.titleCase(formatMessage({id: 'common.time_entry'}))}
								<FlexRow justifyContent={'flex-end'}>
									<ShortcutSignifier name={'='} />
								</FlexRow>
							</DropdownHandler.Item>
						</>
					)}
					{preventCreateTimeOff ? null : (
						<DropdownHandler.Item onClick={handleCreateTimeOff} data-cy={'create-new-time-off'}>
							<Icon icon="timeOff" size="m" />
							{formatMessage({id: 'common.time_off'})}
						</DropdownHandler.Item>
					)}
					{!isClientUser() && CompanySetupUtil.hasCreateExpense() && (
						<>
							<DropdownHandler.Item onClick={handleCreateExpenseItem} data-cy={'create-new-expense'}>
								<Icon icon="expense" size="m" />
								{formatMessage({id: 'header.expense-item'})}
							</DropdownHandler.Item>
							<DropdownHandler.Separator />
						</>
					)}
					{hasPermission(PERMISSION_TYPE.CLIENT_CREATE) &&
						!isFeatureHidden(HIDDEN_FEATURES.CLIENT) &&
						!hasModule(MODULE_TYPES.SAGE_PSA_RESTRICTED) && (
							<DropdownHandler.Item onClick={handleCreateClient} data-cy={'create-new-client'}>
								<Icon icon="client" size="m" />
								{formatMessage({id: 'common.client'})}
							</DropdownHandler.Item>
						)}
					{hasFeatureFlag(FEATURE_FLAGS.NEW_CLIENT_CONTACTS_FEATURE) &&
						hasModule(MODULE_TYPES.NEW_CLIENT_CONTACTS_FEATURE) &&
						hasPermission(PERMISSION_TYPE.CLIENT_CREATE) && (
							<ClientContactMenuItem onPress={handleCreateClientContact} />
						)}
					{/* OPPORTUNITY_CREATE is ADDITIONAL to PROJECTS_CREATE, not a replacement —
						the server enforces the same pair. */}
					{hasPermission(PERMISSION_TYPE.PROJECTS_CREATE) &&
						hasPermission(PERMISSION_TYPE.OPPORTUNITY_CREATE) &&
						hasFeatureFlag(FEATURE_FLAGS.OPPORTUNITIES) &&
						hasModule(MODULE_TYPES.OPPORTUNITIES) && (
							<DropdownHandler.Item onClick={handleCreateOpportunity} data-cy={'create-new-opportunity'}>
								<Icon icon="project" size="m" />
								{formatMessage({id: 'common.opportunity'})}
							</DropdownHandler.Item>
						)}
					{hasPermission(PERMISSION_TYPE.INSIGHTS_CREATE) && !hasModule(MODULE_TYPES.SAGE_PSA_RESTRICTED) && (
						<DropdownHandler.Item onClick={handleCreateInsight} data-cy={'create-new-report'}>
							<Icon icon="report" size="m" />
							{formatMessage({id: 'common.report'})}
						</DropdownHandler.Item>
					)}
					{hasPermission(PERMISSION_TYPE.PERSON_CREATE) && !hasModule(MODULE_TYPES.SAGE_PSA_RESTRICTED) && (
						<DropdownHandler.Item onClick={handleCreateTeamMember} cy={'create-new-team-member'}>
							<Icon icon="admin" size="m" />
							{formatMessage({id: 'common.team_member'})}
						</DropdownHandler.Item>
					)}
					{isTicketingAvailable() && hasPermission(PERMISSION_TYPE.TICKETS_CREATE) && (
						<>
							<DropdownHandler.Separator />
							<DropdownHandler.Item onClick={handleCreateTicket} data-cy={'create-new-ticket'}>
								<Icon icon="ticket" size="m" />
								{formatMessage({id: 'common.ticket'})}
							</DropdownHandler.Item>
						</>
					)}
				</DropdownHandler.Content>
			</DropdownHandler>
			{modalState.type === 'createClient' && (
				<CreateClientModal
					companyId={company.id}
					existingClientNames={[]}
					onClose={() => setModalState({type: 'none'})}
					onClientCreated={handleClientCreated}
				/>
			)}
			{modalState.type === 'createContactConfirm' && (
				<CreateContactConfirmModal
					clientName={modalState.clientName}
					onAddContacts={() => {
						if (!modalState.clientId) {
							// Defensive: should never happen; CreateClientMutation always returns an id.
							setModalState({type: 'none'});
							return;
						}
						setModalState({
							type: 'createContact',
							clientId: modalState.clientId,
							clientName: modalState.clientName,
						});
					}}
					onSkip={() => setModalState({type: 'none'})}
				/>
			)}
			{modalState.type === 'createContact' && (
				<ClientContactModalContainer
					key={modalState.clientId}
					mode="contact"
					entryPoint="header"
					clientId={modalState.clientId}
					preSelectedClientName={modalState.clientName}
					onClose={() => setModalState({type: 'none'})}
				/>
			)}
		</>
	);
};
const CreateButtonQuery = graphql`
	query CreateButton_Query($projectId: String) {
		viewer {
			actualPersonId
			component(name: "top_navigation_create_button")
			...CreateButton_viewer @arguments(projectId: $projectId)
		}
	}
`;

export {CreateButtonQuery};

export default withRouter(
	createFragmentContainer(CreateButton, {
		viewer: graphql`
			fragment CreateButton_viewer on Viewer @argumentDefinitions(projectId: {type: "String"}) {
				id
				actualPersonId
				availableFeatureFlags {
					# Fetching this field despite it not being used to force the CreateButton component to rerender when feature flags are updated, as the list otherwise does not respect feature flag updates
					key
				}
				company {
					id
					bambooHREnabled
					hiBobEnabled
					hiBobConnectionEnabled
				}
				project(id: $projectId) {
					id
					stage {
						id
						name
						profile
					}
				}
			}
		`,
	})
);
